// Верхняя панель корневых вкладок. Закреплена сверху — не уезжает при прокрутке и автопрокрутке «Матчей».
// Есть серверная шапка (config.navigation.headers.<вкладка>, гид «navigation.headers», с 10.10 у всех вкладок) —
// рисуем ЕЁ: слоты left · center · right, кнопки в порядке массива. Без заголовка — вид «Матч-центра» (капсула,
// поле поиска); с kind `title` — крупный заголовок, как у своей. Незнакомый kind/action — молча пропускаем.
// `visible: false` — шапки нет вовсе (Новости: экран начинается полосой вкладок). Нет шапки или она пуста — своя:
// название вкладки + поиск + меню.
import { track } from '../analytics/track';
import { useQuery } from '@tanstack/react-query';
import { useLayoutEffect, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../api/client';
import type { HeaderItem } from '../api/types';
import { t } from '../i18n/i18n';
import { paths } from '../nav';
import { setMatchesUi, useMatchesUi } from '../screens/matchesUi';
import { useHasSession } from '../session/favorites';
import { haptic } from '../telegram/webapp';
import calendarDots from '../assets/icons/calendar-dots.svg';
import listIcon from '../assets/icons/list.svg';
import magnifyingGlass from '../assets/icons/magnifying-glass.svg';
import { TabIcon } from './Icon';
import { MaskIcon } from './MenuCell';
import { trackQuizOpen } from '../screens/quiz/quizEvents';
import { runSearch } from './searchOverride';
import { anchorAttr } from './spotlight/anchors';
import { useTourAnchors } from '../tour/launch';
import styles from './AppBar.module.css';

const SLOTS = ['left', 'center', 'right'] as const;

/** Высота шапки без safe area — в --appbar-height: под ней закрепляются вкладки дней «Матчей». */
const HEIGHT = { server: 64, own: 52, centered: 44, none: 0 } as const;

// Шапка по центру (Figma «Избранное», Nav Bar I1073:20355;13332:46982, HQ-1887): экран просит её useCenteredHeader —
// панель 44, слоты 40 × 40 по бокам, заголовок 15/500 по центру; справа только лупа (меню — в шапке «Матчей»).
let centeredScreens = 0;
const centeredListeners = new Set<() => void>();
function subscribeCentered(listener: () => void) {
  centeredListeners.add(listener);
  return () => { centeredListeners.delete(listener); };
}

/** Экран просит шапку по центру, пока смонтирован; метка data-header-centered на <html> — для стилей и замеров. */
export function useCenteredHeader() {
  useLayoutEffect(() => {
    const root = document.documentElement;
    centeredScreens += 1;
    root.setAttribute('data-header-centered', '');
    centeredListeners.forEach((l) => l());
    return () => {
      centeredScreens -= 1;
      if (!centeredScreens) root.removeAttribute('data-header-centered');
      centeredListeners.forEach((l) => l());
    };
  }, []);
}

function useCentered(): boolean {
  return useSyncExternalStore(subscribeCentered, () => centeredScreens > 0, () => false);
}

/** Кнопки шапки по центру: заголовок и лупа; меню и прочие кнопки — нет (макет: левый слот пуст, справа одна лупа).
 *  Исключение — значок квиза дня (HQ-2329): он на всех вкладках, справа после лупы, в порядке сервера. */
export function centeredItems(items: HeaderItem[]): HeaderItem[] {
  return items.filter((i) => i.kind === 'title' || i.action?.type === 'search' || isQuizButton(i));
}

/** Кнопка квиза дня: open с адресом набора (/api/v5/mobile/quiz/{sport}/). */
const isQuizButton = (i: HeaderItem) => i.kind === 'button' && i.action?.type === 'open'
  && Boolean(i.action.endpoint && routeFromHeaderEndpoint(i.action.endpoint)?.startsWith('/quiz'));

export interface ServerHeader { items?: HeaderItem[]; visible?: boolean }
export type HeaderMode = 'none' | 'own' | 'server' | 'titled';

/** Какую шапку рисовать: `visible: false` — никакой; нет/пусто/всё незнакомо — свою; есть `title` — крупный заголовок. */
export function headerMode(header: ServerHeader | undefined): HeaderMode {
  if (header?.visible === false) return 'none';
  const known = (header?.items ?? []).filter(isKnown);
  if (!known.length) return 'own';
  return known.some((i) => i.kind === 'title') ? 'titled' : 'server';
}

/** Якорь тура по приложению (HQ-2338) на нарисованном пункте шапки: header.{вкладка}.{ключ пункта}. */
const headerAnchor = (tab: string | undefined, item: HeaderItem) => anchorAttr(tab ? `header.${tab}.${item.key}` : null);

/** tab — ключ вкладки шапки: по нему якоря пунктов для тура (только на время тура). */
export function AppBar({ title, header, tab: tabKey }: { title: string; header?: ServerHeader; tab?: string }) {
  const tab = useTourAnchors() ? tabKey : undefined;
  const mode = headerMode(header);
  const centered = useCentered() && mode === 'titled';
  useLayoutEffect(() => {
    const height = mode === 'none' ? HEIGHT.none : mode === 'server' ? HEIGHT.server : centered ? HEIGHT.centered : HEIGHT.own;
    document.documentElement.style.setProperty('--appbar-height', `${height}px`);
  }, [mode, centered]);
  if (mode === 'none') return null;
  if (mode === 'own') return <OwnBar title={title} />;
  const known = (header?.items ?? []).filter(isKnown);
  if (centered) return <CenteredBar items={centeredItems(known)} title={title} tab={tab} />;
  const titled = mode === 'titled';
  return (
    <header className={titled ? `${styles.bar} ${styles.titled}` : `${styles.bar} ${styles.server}`}>
      {SLOTS.map((slot) => {
        const items = known.filter((i) => i.slot === slot);
        if (!items.length) return slot === 'center' && !titled ? <span key={slot} className={styles.center} /> : null;
        const body = items.map((item) => <HeaderControl key={item.key} item={item} title={title} anchor={headerAnchor(tab, item)} />);
        // Заголовок — вне капсулы, во всю свободную ширину (Figma: large title + кнопки справа)
        if (titled && slot !== 'right') return <div key={slot} className={styles.center}>{body}</div>;
        // LIVE и календарь в макете — одна серая «капсула» слева
        return slot === 'left'
          ? <div key={slot} className={styles.capsule}>{body}</div>
          : <div key={slot} className={slot === 'center' ? styles.center : styles.right}>{body}</div>;
      })}
    </header>
  );
}

function CenteredBar({ items, title, tab }: { items: HeaderItem[]; title: string; tab?: string }) {
  const navigate = useNavigate();
  const heading = items.find((i) => i.kind === 'title');
  const buttons = items.filter((i) => i.kind !== 'title');
  // слева пустые слоты по числу кнопок справа — заголовок остаётся по центру и со значком квиза
  const spacers = Math.max(1, buttons.length);
  // лупа — как у кнопки поиска шапок вкладок: экран вкладки перехватывает (поиск «Избранного» на месте, searchOverride)
  return (
    <header className={`${styles.bar} ${styles.centered}`}>
      {Array.from({ length: spacers }, (_, i) => <span key={`spacer-${i}`} className={styles.slot} aria-hidden="true" />)}
      <h1 className={styles.title}>{heading?.label || title}</h1>
      {buttons.length === 0 && <span className={styles.slot} />}
      {buttons.map((item) => (
        <span key={item.key} className={styles.slot}>
          {item.action?.type === 'search'
            ? (
              <button className={styles.icon} aria-label={item.label ?? t('mc_search')} {...headerAnchor(tab, item)} onClick={() => runSearch(item.action?.endpoint, () => navigate(paths.search(item.action?.endpoint)))}>
                <MaskIcon src={magnifyingGlass} size={24} />
              </button>
            )
            : <HeaderButton item={item} anchor={headerAnchor(tab, item)} />}
        </span>
      ))}
    </header>
  );
}

function OwnBar({ title }: { title: string }) {
  const navigate = useNavigate();
  return (
    <header className={styles.bar}>
      <h1 className={styles.title}>{title}</h1>
      <button className={styles.icon} aria-label={t('mc_search')} onClick={() => runSearch(undefined, () => navigate(paths.search()))}><MaskIcon src={magnifyingGlass} size={22} /></button>
      <button className={styles.icon} aria-label={t('settings')} onClick={() => navigate('/settings')}><MaskIcon src={listIcon} size={24} /></button>
    </header>
  );
}

/** Что умеем нарисовать: остальное — молча мимо (контракт). */
export function isKnown(item: HeaderItem): boolean {
  const type = item.action?.type;
  switch (item.kind) {
    case 'toggle': return type === 'filter' && Boolean(item.action?.field);
    case 'date_picker': return type === 'date_picker';
    case 'title': return true;
    case 'search': return type === 'search';
    case 'menu': return type === 'settings';
    case 'button':
      if (type === 'search' || type === 'settings') return true;
      return type === 'open' && Boolean(item.action?.endpoint && routeFromHeaderEndpoint(item.action.endpoint));
    default: return false;
  }
}

function HeaderControl({ item, title, anchor }: { item: HeaderItem; title: string; anchor: Record<string, string> }) {
  const navigate = useNavigate();
  const ui = useMatchesUi();
  switch (item.kind) {
    case 'title':
      return <h1 className={styles.title} {...anchor}>{item.label || title}</h1>;
    case 'toggle':
      return (
        <button className={`${styles.live} ${ui.liveOnly ? styles.liveOn : ''}`} aria-pressed={ui.liveOnly} {...anchor}
          onClick={() => {
            haptic.selection();
            track('match_center_filter', { filter: ui.liveOnly ? 'all' : 'live' });
            setMatchesUi({ liveOnly: !ui.liveOnly, liveField: item.action!.field! });
          }}>
          <span className={styles.liveDot} aria-hidden="true" />{item.label ?? t('mc_live')}
        </button>
      );
    case 'date_picker':
      return (
        <button className={styles.icon} aria-label={item.label ?? item.key} {...anchor} onClick={() => setMatchesUi({ calendarOpen: true })}>
          <MaskIcon src={calendarDots} size={24} />
        </button>
      );
    case 'search':
      return (
        <button className={styles.searchField} {...anchor} onClick={() => navigate(paths.search(item.action?.endpoint))}>
          <MaskIcon src={magnifyingGlass} size={20} /><span>{item.label ?? t('mc_search')}</span>
        </button>
      );
    case 'menu':
      return <button className={styles.icon} aria-label={item.label ?? t('settings')} {...anchor} onClick={() => navigate('/settings')}><MaskIcon src={listIcon} size={24} /></button>;
    default:
      // Кнопка-лупа шапок вкладок: наш экран поиска с адресом сервера как есть (экран вкладки может перехватить — searchOverride)
      if (item.action?.type === 'search') {
        return (
          <button className={styles.icon} aria-label={item.label ?? t('mc_search')} {...anchor} onClick={() => runSearch(item.action?.endpoint, () => navigate(paths.search(item.action?.endpoint)))}>
            <MaskIcon src={magnifyingGlass} size={22} />
          </button>
        );
      }
      if (item.action?.type === 'settings') {
        return <button className={styles.icon} aria-label={item.label ?? t('settings')} {...anchor} onClick={() => navigate('/settings')}><MaskIcon src={listIcon} size={24} /></button>;
      }
      return <HeaderButton item={item} anchor={anchor} />;
  }
}

/** Экран приложения по endpoint кнопки; незнакомый адрес — кнопки нет. */
export function routeFromHeaderEndpoint(endpoint: string, title?: string): string | null {
  const path = endpoint.split('?')[0]!;
  if (/\/game\/[a-z]+\/?$/.test(path)) {
    return `/game?path=${encodeURIComponent(endpoint)}${title ? `&title=${encodeURIComponent(title)}` : ''}`;
  }
  // квиз дня (HQ-2329): /api/v5/mobile/quiz/{sport}/ — экран набора, адрес как есть
  if (/\/quiz\/[a-z]+\/?$/.test(path)) return paths.quiz(endpoint);
  return null;
}

/** Значение по пути через точку («streak.current») из ответа бейджа. */
export function pick(data: unknown, path: string | undefined): unknown {
  if (!path) return undefined;
  return path.split('.').reduce<unknown>((v, k) => (v && typeof v === 'object' ? (v as Record<string, unknown>)[k] : undefined), data);
}

function HeaderButton({ item, anchor }: { item: HeaderItem; anchor: Record<string, string> }) {
  const navigate = useNavigate();
  const route = routeFromHeaderEndpoint(item.action!.endpoint!, item.label);
  const has = useHasSession();
  // бейдж — личный (me/ без аккаунта отвечает 401): спрашиваем, только когда сессия уже есть
  const badge = useQuery({
    queryKey: ['header-badge', item.badge?.source, has],
    queryFn: ({ signal }) => apiRequest<unknown>('GET', item.badge!.source, { auth: true, signal }),
    enabled: Boolean(item.badge?.source) && has,
    staleTime: 60_000,
  });
  if (!route) return null;
  const value = pick(badge.data, item.badge?.value);
  const alert = pick(badge.data, item.badge?.alert) === true;
  return (
    <button className={styles.icon} aria-label={item.label ?? item.key} {...anchor} onClick={() => {
      if (route.startsWith('/quiz')) trackQuizOpen('header');
      navigate(route);
    }}>
      <TabIcon name={item.icon ?? item.key} filled={false} />
      {typeof value === 'number' && value > 0 && (
        <span className={item.badge?.style === 'level' ? styles.level : styles.badge}>{value}</span>
      )}
      {alert && <span className={styles.alert} aria-hidden="true" />}
    </button>
  );
}
